import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArchiveIcon from '@mui/icons-material/Archive';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DownloadIcon from '@mui/icons-material/Download';
import ImportLauncherDialog from '../WorkspaceStudio/components/ImportLauncherDialog';
import { DataGrid } from '@mui/x-data-grid';
import { useAuth } from '../../contexts/AuthContext';
import { useDealerAdmin } from '../../contexts/DealerAdminContext';
import MainLayout from '../../components/layout/MainLayout';
import {
  listWorkspaces,
  createWorkspace,
  createStarterHome,
  duplicateWorkspace,
  archiveWorkspace,
} from '../../api/workspaceApi';

const ALLOWED_ROLES = ['admin', 'superadmin', 'tenant_admin'];

export default function WorkspaceListPage() {
  const navigate = useNavigate();
  const { userData, role } = useAuth();
  const dealerAdmin = useDealerAdmin();
  const tenantId = dealerAdmin.tenantId || userData?.tenantId;
  const userRole = role || userData?.role;
  const canManage = ALLOWED_ROLES.includes(userRole) || dealerAdmin.active;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '' });
  const [createLoading, setCreateLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [snack, setSnack] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listWorkspaces(tenantId);
      const items = res?.data?.items ?? res?.items ?? [];
      setRows(items);
    } catch (e) {
      setError(e?.message || 'Failed to load home designer layouts');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleStarter = async () => {
    setCreateLoading(true);
    try {
      const res = await createStarterHome();
      const created = res?.data?.created;
      await load();
      setSnack({
        severity: 'success',
        message: created
          ? 'Starter Home published and assigned as this tenant’s default.'
          : 'Starter Home is already this tenant’s default.',
      });
    } catch (e) {
      const message = String(e?.message || '');
      const exists = message.includes('already has a Home');
      setSnack({
        severity: exists ? 'info' : 'error',
        message: exists ? 'This tenant already has a Home assigned. It was left unchanged.' : (e?.message || 'Could not create the starter Home.'),
      });
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!createForm.name.trim()) return;
    setCreateLoading(true);
    try {
      await createWorkspace({ name: createForm.name.trim(), description: createForm.description.trim() });
      setCreateOpen(false);
      setCreateForm({ name: '', description: '' });
      await load();
      setSnack({ severity: 'success', message: 'Home designer layout created.' });
    } catch (e) {
      setSnack({ severity: 'error', message: e?.message || 'Failed to create home designer layout.' });
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDuplicate = async (id, name) => {
    setActionLoading((prev) => ({ ...prev, [id]: 'duplicate' }));
    try {
      await duplicateWorkspace(id, `${name} (Copy)`);
      await load();
      setSnack({ severity: 'success', message: 'Layout duplicated.' });
    } catch (e) {
      setSnack({ severity: 'error', message: e?.message || 'Duplicate failed.' });
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: null }));
    }
  };

  const handleArchive = async (id) => {
    if (!window.confirm('Archive this layout? It will no longer be visible to users.')) return;
    setActionLoading((prev) => ({ ...prev, [id]: 'archive' }));
    try {
      await archiveWorkspace(id);
      await load();
      setSnack({ severity: 'success', message: 'Layout archived.' });
    } catch (e) {
      setSnack({ severity: 'error', message: e?.message || 'Archive failed.' });
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: null }));
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 2,
      renderCell: ({ row }) => (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => navigate(`/admin/workspaces/${row.id}`)}
        >
          {row.name}
        </Typography>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 3,
      renderCell: ({ value }) => (
        <Typography variant="body2" color="text.secondary" noWrap>{value || '—'}</Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: ({ value }) => (
        <Chip
          label={value}
          size="small"
          color={value === 'active' ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'publishedVersionId',
      headerName: 'Published',
      width: 100,
      renderCell: ({ value }) => (
        <Chip
          label={value ? 'Yes' : 'No'}
          size="small"
          color={value ? 'primary' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'currentDraftVersionId',
      headerName: 'Draft',
      width: 90,
      renderCell: ({ value }) => (
        <Chip
          label={value ? 'Yes' : 'No'}
          size="small"
          color={value ? 'warning' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'updatedAt',
      headerName: 'Last Updated',
      width: 170,
      renderCell: ({ value }) =>
        value ? new Date(value).toLocaleString() : '—',
    },
    {
      field: 'actions',
      headerName: '',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        const busy = actionLoading[row.id];
        return (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Open detail">
              <IconButton size="small" onClick={() => navigate(`/admin/workspaces/${row.id}`)}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Open in Home Designer">
              <IconButton size="small" color="primary" onClick={() => navigate(`/studio/${row.id}`)}>
                <EditNoteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Duplicate">
              <span>
                <IconButton
                  size="small"
                  disabled={!!busy}
                  onClick={() => handleDuplicate(row.id, row.name)}
                >
                  {busy === 'duplicate' ? <CircularProgress size={14} /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Archive">
              <span>
                <IconButton
                  size="small"
                  disabled={!!busy || row.status === 'archived'}
                  onClick={() => handleArchive(row.id)}
                >
                  {busy === 'archive' ? <CircularProgress size={14} /> : <ArchiveIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h5" fontWeight={700}>Home Designer</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage home screen layouts for your tenant. The existing launcher remains active for all unassigned users.
            </Typography>
          </Box>
          {canManage && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={handleStarter}
                disabled={createLoading}
              >
                Create starter Home
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => setImportOpen(true)}
              >
                Import Launcher
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
              >
                New Layout
              </Button>
            </Stack>
          )}
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {snack && (
          <Alert severity={snack.severity} sx={{ mb: 2 }} onClose={() => setSnack(null)}>
            {snack.message}
          </Alert>
        )}

        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            sx={{ border: 0 }}
          />
        </Paper>

        <ImportLauncherDialog
          open={importOpen}
          onClose={() => { setImportOpen(false); load(); }}
          targetTenantId={tenantId}
        />

        {/* Create dialog */}
        <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>New Home Designer Layout</DialogTitle>
          <DialogContent>
            <Stack spacing={2} mt={1}>
              <TextField
                label="Layout Name"
                value={createForm.name}
                onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                required
                autoFocus
                fullWidth
              />
              <TextField
                label="Description (optional)"
                value={createForm.description}
                onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
                fullWidth
                multiline
                rows={2}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)} disabled={createLoading}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={createLoading || !createForm.name.trim()}
            >
              {createLoading ? <CircularProgress size={18} /> : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
}
